
//============================================================================
// Name        : integrations.ts
// Purpose     : Implement integrations page dynamic behaviors
// Created     : 2020-03-23
// Author      : Senitron Team
// Copyright   : Copyright 2020 Senitron Corporation. All rights reserved.
//============================================================================

import {InitPage} from "../index";
import { showIntegrationToast, showIntegrationFlash } from "./integration_toast";

interface ConfirmOptions {
    title: string;
    message: string;
    confirmText: string;
    variant?: "danger" | "primary";
}

export function snConfirm(options: ConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
        const previousFocus = document.activeElement as HTMLElement;
        const dialog = $(`
            <div class="sn-confirm ${options.variant === "primary" ? "primary" : ""}" role="alertdialog" aria-modal="true" aria-labelledby="sn-confirm-title" aria-describedby="sn-confirm-text">
                <div class="sn-confirm-box">
                    <div class="sn-confirm-icon">
                        <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v9"></path><circle cx="12" cy="18" r="1"></circle></svg>
                    </div>
                    <h3 id="sn-confirm-title"></h3>
                    <p id="sn-confirm-text"></p>
                    <div class="sn-confirm-actions">
                        <button type="button" class="sn-confirm-btn sn-confirm-cancel">Cancel</button>
                        <button type="button" class="sn-confirm-btn sn-confirm-ok"></button>
                    </div>
                </div>
            </div>`);
        dialog.find("h3").text(options.title);
        dialog.find("p").html(options.message);
        dialog.find(".sn-confirm-ok").text(options.confirmText);

        const close = (result: boolean) => {
            $(document).off("keydown.snConfirm");
            const openModal = $(".modal.in").last().data("bs.modal");
            if (openModal) openModal.enforceFocus();
            dialog.removeClass("open");
            setTimeout(() => dialog.remove(), 200);
            if (previousFocus) previousFocus.focus();
            resolve(result);
        };

        dialog.on("click", (e) => { if (e.target === dialog[0]) close(false); });
        dialog.find(".sn-confirm-cancel").on("click", () => close(false));
        dialog.find(".sn-confirm-ok").on("click", () => close(true));
        $(document).on("keydown.snConfirm", (e) => {
            if (e.key === "Escape") { e.stopImmediatePropagation(); close(false); }
            if (e.key === "Tab") {
                const buttons = dialog.find("button").toArray() as HTMLElement[];
                const index = buttons.indexOf(document.activeElement as HTMLElement);
                e.preventDefault();
                buttons[(index + (e.shiftKey ? buttons.length - 1 : 1)) % buttons.length].focus();
            }
        });

        $(document).off("focusin.bs.modal");
        $("body").append(dialog);
        requestAnimationFrame(() => dialog.addClass("open"));
        dialog.find(".sn-confirm-cancel").trigger("focus");
    });
}

(window as any).snConfirm = snConfirm;

const REQUIRED_FIELDS: { [name: string]: string[] } = {
    treez: ["username", "password", "api_key", "dispensary_name", "ext_client_id", "license"],
    odoo: ["url", "username", "password", "database"],
    shopify: ["api_secret_key", "token", "domain_prefix"],
    heartland: ["domain_prefix", "token"],
    twilio: ["token", "properties[twilio][account_sid]", "properties[twilio][source_number]"],
    quickbook: ["ext_client_id", "api_secret_key"],
    quickbooks_desktop: ["url"],
    netsuite: ["domain_prefix", "ext_client_id", "ext_client_secret", "api_key", "api_secret_key", "netsuite_full_catalog_script", "netsuite_catalog_script"],
    retail_express: ["domain_prefix", "username", "password", "ext_client_id", "api_key"],
    microsoft_azure: ["domain_prefix", "queue_name", "token"],
    d365: ["url", "port", "username", "password", "company", "properties[d365][instance]"],
    custom_d365: ["ext_client_id", "ext_client_secret", "domain_prefix", "scope"],
    retail_pro_prism: ["username", "password"],
    keonn: ["properties[keonn][workspace_token]", "properties[keonn][portal_subdomain]", "properties[keonn][api_username]", "properties[keonn][api_password]",
            "url", "port", "username", "password", "properties[keonn][kafka_topic]"],
    microsoftNav2019: ["url", "port", "username", "password"],
    redo: ["token", "properties[redo][store_id]"],
    lightspeed: ["account_id", "username", "password"],
    ls_pos: ["account_id"],
    vend: ["domain_prefix", "token"],
    acumatica: ["domain_prefix", "username", "password", "ext_client_id", "account_id"],
    counterpoint: ["url", "port", "username", "password"],
    auditlogs: []
};

const GS1_VALIDATED_FIELDS: { [integrationName: string]: string[] } = {
    Vend: ["upc", "ean", "isbn", "custom"],
    Lightspeed: ["upc"],
    Shopify: ["upc", "gs1upc"],
    Netsuite: ["upc"],
    Odoo: ["upc"]
};

const GS1_FIELD_LABELS: { [field: string]: string } = { upc: "UPC", ean: "EAN", isbn: "ISBN", custom: "Custom", gs1upc: "GS1 UPC" };

class Integrations {
    constructor () {
        $('.show-message').on('click', function(){
            showIntegrationToast("Please resolve the warnings and retry", "error");
        });
    }

    private currentCategory: string | null = null;
    private initialValues = new Map<Element | string, string>();
    private connecting = false;

    init() {
        $(".edit-integrations").on("click", (e) => this.onSelectEditDialog(e.currentTarget));
        $(".js-oauth-connect").on("click", (e) => this.openOauthConnect($(e.currentTarget)));
        $("#final-step-form").on("submit", (e) => this.saveFinalStep(e));
        $("#setupFinalStepModal").on("click", "[data-reveal]", (e) => {
            const btn = $(e.currentTarget);
            const field = btn.siblings(".fld").toggleClass("masked");
            btn.text(field.hasClass("masked") ? "Show" : "Hide");
        });
        $(document).on("hidden.bs.modal", ".modal", (e) => {
            if (e.target !== $("#editIntegration")[0] && $("#editIntegration").hasClass("in")) $("body").addClass("modal-open");
        });
        $(document).on("integration-webhooks:rendered", () => this.initIntegrationWebhooks());
        $(document).on("change", "#integration-webhook-event", (e) => {
            $("#add-integration-webhook").prop("disabled", !$(e.currentTarget).val());
        });
        $(document).on("click", "#add-integration-webhook", (e) => this.addIntegrationWebhook($(e.currentTarget)));
        $(document).on("click", ".delete-integration-webhook", (e) => this.deleteIntegrationWebhook($(e.currentTarget)));
        $(document).on("integrations:subview-shown", (_e: any, id: string) => {
            if (id !== "d365TestView") return;
            const testForm = $("#d365-form");
            if (testForm.data("with-form-values") !== true) return;
            testForm.find("input.d365-form-value").remove();
            const settings = $("#editIntegration .modal-content > form");
            ["url", "port", "username", "password", "company", "properties[d365][type]", "properties[d365][instance]"].forEach((name) => {
                const field = settings.find(`[name="${name}"]`).not("[type=hidden]").first();
                testForm.append($("<input>", { type: "hidden", class: "d365-form-value", name, value: String(field.val() || "") }));
            });
        });
        $(document).on("submit", "#d365-advanced-form", (e) => this.saveD365Advanced(e));
        $(document).on("submit", "#twilio-test-form", (e) => this.sendTwilioTest(e));
        $(document).on("submit", "#d365-form", (e) => {
            const form = $(e.currentTarget);
            const submit = form.find("#d365-test-submit");
            if (submit.prop("disabled")) {
                e.preventDefault();
                return;
            }
            form.find(".d365-test-state").hide();
            form.find('.d365-test-state[data-state="running"]').css("display", "flex");
            submit.prop("disabled", true);
        });
        $(document).on("ajax:error", "#d365-form", (e) => {
            const form = $(e.currentTarget);
            form.find(".d365-test-state").hide();
            form.find(".d365-test-error").text("The test request didn't finish.");
            form.find('.d365-test-state[data-state="failed"]').css("display", "flex");
            form.find("#d365-test-submit").prop("disabled", false);
        });
        $(document).on("ajax:error", ".card-actions a[data-remote]", (e) => {
            const label = String($(e.currentTarget).attr("aria-label") || "this window");
            showIntegrationToast(`Couldn't open ${label}. Please try again.`, "error");
        });
        $("#oauth-connect-form").on("submit", () => {
            const submit = $("#oauth-connect-submit");
            submit.prop("disabled", true).text(submit.data("busy-label"));
        });
        $("#copy-key").on("click", () => this.copyIntegrationKey());
        this.addCopyButtons();
        showIntegrationFlash();
        $("#editIntegration").on("change", "input:checkbox[name=gs_one_standard], #gs_one_standard_field", () => this.updateGs1Validation(true));
        $(".js-remove-integration").on("click", (e) => this.removeIntegration($(e.currentTarget)));

        const modal = $("#editIntegration");
        modal.find(".modal-content > form").on("submit", (e) => this.validateSettingsForm(e));
        $(window).on("pageshow", () => this.updateUnsavedStatus());
        modal.on("input", "#properties_keonn_workspace_token", (e) => this.syncKeonnSubdomain($(e.currentTarget)));
        modal.on("click", ".keonn-test-connection", (e) => this.testKeonnConnection($(e.currentTarget)));
        modal.on("change", "#properties_keonn_send_movement_notifications_to_eos", () => this.toggleMovementEosFields());
        modal.on("click", ".keonn-test-results .test-close", () => $("#editIntegration .keonn-test-results").prop("hidden", true));
        modal.on("change", "#properties_keonn_portal_subdomain", (e) => {
            const field = $(e.currentTarget);
            field.val(this.keonnSubdomain(String(field.val() || "")));
        });
        modal.on("click", ".cat-btn", (e) => {
            $("#int-search").val("");
            this.selectCategory(String($(e.currentTarget).data("cat")));
        });
        modal.on("input", "#int-search", () => this.searchSettings());
        modal.on("keydown", "#int-search", (e) => {
            if (e.key === "Enter") e.preventDefault();
        });
        modal.on("click", "[data-reveal]", (e) => {
            const btn = $(e.currentTarget);
            const field = btn.siblings(".fld").toggleClass("masked");
            btn.text(field.hasClass("masked") ? "Show" : "Hide");
        });
        modal.on("change", "input:checkbox[name=ls_enable_automatic_sync]", (e) => {
            $("input:checkbox[name=ls_enable_automatic_sync]").prop("checked", (e.currentTarget as HTMLInputElement).checked);
        });
        modal.on("input change", "input, select, textarea", () => this.updateUnsavedStatus());
        modal.on("change", ".int-cat input, .enabled_field input", () => {
            $(".shopify-webhook-configs").promise().done(() => this.refreshCategories());
        });
    }

    removeIntegration(button: JQuery<HTMLElement>) {
        const name = String(button.data("name") || "");
        snConfirm({
            title: `Remove the ${name} integration?`,
            message: `Senitron stops syncing with <b>${$("<div>").text(name).html()}</b> and this integration's settings are deleted. You can connect it again later.`,
            confirmText: "Remove"
        }).then((confirmed) => {
            if (!confirmed) return;
            button.prop("disabled", true);
            const form = $("<form>", { method: "post", action: button.data("url") }).hide();
            form.append($("<input>", { type: "hidden", name: "_method", value: "delete" }));
            form.append($("<input>", { type: "hidden", name: $('meta[name="csrf-param"]').attr("content"), value: $('meta[name="csrf-token"]').attr("content") }));
            $("body").append(form);
            form.trigger("submit");
        });
    }

    updateGs1Validation(userChange: boolean) {
        const validated = GS1_VALIDATED_FIELDS[String($("#editIntegration").data("gs1-integration") || "")];
        if (!validated) return;
        const gs1On = $("input:checkbox[name=gs_one_standard]").is(":checked");
        const field = String($("#gs_one_standard_field").val() ?? "");
        const show = gs1On && validated.indexOf(field) !== -1;
        $(".gs_one_standard_text_field").toggleClass("hide", !gs1On);
        $(".disable_upc_validation").removeAttr("style").toggleClass("hide", !show);
        $(".disable-upc-validation-label").text(`Disable ${GS1_FIELD_LABELS[field] || "UPC"} validation`);
        const disable = $("input:checkbox[name=disable_upc_validation]");
        if (!show && userChange && disable.is(":checked")) disable.prop("checked", false).trigger("change");
        this.updateCategoryBadges();
    }

    addCopyButtons() {
        const icon = String($("#int-copy-icon").html() || "");
        $('#editIntegration .int-cat[data-cat="connection"], #editIntegration .int-cat[data-cat="keonn_portal"]').find("input.fld, textarea.fld").not("[type=hidden], [type=checkbox], [type=radio]").each((_, el) => {
            const field = $(el);
            const button = $("<button>", { type: "button", class: "btn-outline btn-copy" }).html(icon);
            button.on("click", () => this.copyFieldValue(field, button));
            if (field.parent().hasClass("secret")) {
                field.parent().append(button);
            } else {
                field.wrap('<div class="with-copy"></div>').after(button);
            }
        });
        this.updateCopyButtons();
    }

    updateCopyButtons() {
        $("#editIntegration .btn-copy").each((_, btn) => {
            const field = $(btn).parent().find("input.fld, textarea.fld").first();
            const label = this.fieldLabel(field);
            $(btn).prop("disabled", String(field.val() ?? "").trim() === "").attr({ title: `Copy ${label}`, "aria-label": `Copy ${label}` });
        });
    }

    fieldLabel(field: JQuery<any>) {
        return String(field.closest(".form-group").find("label").first().text().trim() || "value");
    }

    copyFieldValue(field: JQuery<any>, button: JQuery<any>) {
        const label = this.fieldLabel(field);
        const value = String(field.val() ?? "");
        if (!value.trim()) return;
        const copied = navigator.clipboard ? navigator.clipboard.writeText(value) : Promise.reject();
        copied
            .then(() => {
                button.addClass("copied");
                setTimeout(() => button.removeClass("copied"), 1500);
                showIntegrationToast(`${label} copied`);
            })
            .catch(() => showIntegrationToast(`Couldn't copy the ${label}. Select it and copy it manually.`, "error"));
    }

    copyIntegrationKey() {
        const key = $("#int-key").text().trim();
        const label = $("#copy-label");
        const original = label.data("label") || label.text();
        label.data("label", original);
        const copied = navigator.clipboard ? navigator.clipboard.writeText(key) : Promise.reject();
        copied
            .then(() => {
                label.text("Copied");
                setTimeout(() => label.text(original), 2000);
            })
            .catch(() => showIntegrationToast("Couldn't copy the key. Select it and copy it manually.", "error"));
    }

    openOauthConnect(button: JQuery<HTMLElement>) {
        const modal = $("#oauthConnectModal");
        const name = String(button.data("display-name") || "");
        const logo = String(button.data("logo") || "");
        const multiLocation = button.data("multi-location") === "Y";
        const submit = $("#oauth-connect-submit");
        modal.find(".oauth-connect-name").text(name);
        modal.find(".oauth-connect-logo").each((_, box) => {
            $(box).empty().append(logo
                ? $("<img>", { src: logo, alt: `${name} logo` })
                : $("<div>", { class: "mono-badge", role: "img", "aria-label": `${name} logo`, text: name.slice(0, 2).toUpperCase() }));
        });
        $("#oauth-connect-field-name").val(String(button.data("name")));
        $("#oauth-connect-field-multi").val(String(button.data("multi-location") || ""));
        modal.find(".oauth-connect-location").toggle(multiLocation);
        const location = modal.find("#oauth-connect-location") as any;
        if (!location.hasClass("select2-hidden-accessible")) {
            location.select2({ width: "100%", dropdownParent: modal.find(".modal-dialog"), minimumResultsForSearch: 8 });
        }
        location.prop("disabled", !multiLocation).trigger("change.select2");
        submit.prop("disabled", multiLocation && submit.data("has-stores") !== true).text(`${submit.data("label")} ${name}`);
        modal.modal("show");
    }

    saveFinalStep(e: any) {
        e.preventDefault();
        const form = $(e.currentTarget);
        const button = form.find("#final-step-submit");
        if (button.prop("disabled")) return;
        const error = form.find(".final-step-error");
        const showError = (message: string) => {
            form.find(".final-step-error-text").text(message);
            error.css("display", "flex");
        };
        error.hide();
        const missing = form.find(":input[required]").filter((_, el) => !(el as HTMLInputElement).checkValidity()).first();
        if (missing.length) {
            (missing[0] as HTMLInputElement).reportValidity();
            return;
        }
        const label = button.text();
        button.prop("disabled", true).text(String(button.data("busy-label")));
        $.ajax({ url: form.attr("action"), method: "POST", dataType: "json", data: form.serialize() })
            .done((resp: any) => {
                if (resp && resp.success === false) {
                    showError(resp.errors || "Please check the email and password.");
                    return;
                }
                form.find(".final-step-fields").hide();
                form.find(".final-step-login").addClass("done").find(".step-mark")
                    .html('<svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 5 5 9-10"></path></svg>');
                form.find(".final-step-done").css("display", "flex");
                form.find(".final-step-cancel").text("Close");
                button.hide();
                form.find(".final-step-next").show().trigger("focus");
            })
            .fail(() => showError("Please try again."))
            .always(() => button.prop("disabled", false).text(label));
    }

    sendTwilioTest(e: any) {
        e.preventDefault();
        const form = $(e.currentTarget);
        const button = form.find("#twilio-test-submit");
        if (button.prop("disabled")) return;
        const settings = $("#editIntegration .modal-content > form");
        const data: { [key: string]: string } = {};
        ["token", "properties[twilio][account_sid]", "properties[twilio][source_number]"].forEach((name) => {
            data[name] = String(settings.find(`[name="${name}"]`).not("[type=hidden]").first().val() || "");
        });
        form.serializeArray().forEach((field: any) => { data[field.name] = field.value; });
        const label = button.text();
        form.find(".twilio-test-state").hide();
        form.find('.twilio-test-state[data-state="sending"]').css("display", "flex");
        button.prop("disabled", true).text(String(button.data("busy-label")));
        $.ajax({ url: form.attr("action"), method: "POST", dataType: "json", data })
            .done(() => {
                form.find(".twilio-test-state").hide();
                form.find('.twilio-test-state[data-state="sent"]').css("display", "flex");
            })
            .fail((xhr: any) => {
                form.find(".twilio-test-state").hide();
                form.find(".twilio-test-error").text((xhr.responseJSON && xhr.responseJSON.error) || "Please try again.");
                form.find('.twilio-test-state[data-state="failed"]').css("display", "flex");
            })
            .always(() => button.prop("disabled", false).text(label));
    }

    saveD365Advanced(e: any) {
        e.preventDefault();
        const form = $(e.currentTarget);
        const button = form.find("#d365-advanced-submit");
        if (button.prop("disabled")) return;
        const label = button.text();
        form.find(".d365-advanced-state").hide();
        button.prop("disabled", true).text(String(button.data("busy-label")));
        $.ajax({ url: form.attr("action"), method: "POST", dataType: "json", data: form.serialize() })
            .done((result: any) => {
                const settings = $("#editIntegration .modal-content > form");
                const sent = form.serializeArray().map((field: any) => field.name);
                Object.keys(result.properties || {}).forEach((key) => {
                    const name = `properties[d365][${key}]`;
                    if (!sent.includes(name)) return;
                    let input = settings.find("input[type=hidden]").filter((_, el) => (el as HTMLInputElement).name === name);
                    if (!input.length) {
                        input = $("<input>", { type: "hidden", name });
                        settings.find(".d365_fields").first().prepend(input);
                    }
                    input.val(String(result.properties[key] ?? ""));
                });
                form.find('.d365-advanced-state[data-state="saved"]').css("display", "flex");
            })
            .fail((xhr: any) => {
                form.find(".d365-advanced-error").text((xhr.responseJSON && xhr.responseJSON.error) || "Please try again.");
                form.find('.d365-advanced-state[data-state="failed"]').css("display", "flex");
            })
            .always(() => button.prop("disabled", false).text(label));
    }

    initIntegrationWebhooks() {
        const modal = $("#integration-webhooks-modal");
        const select = modal.find("#integration-webhook-event") as any;
        select.select2({ width: "100%", dropdownParent: modal.find(".modal-dialog"), minimumResultsForSearch: Infinity });
    }

    reloadIntegrationWebhooks(message: string) {
        const content = $("#integration-webhooks-modal .modal-content");
        return $.ajax({ url: content.data("url"), dataType: "script" })
            .done(() => $("#integration-webhooks-status").text(message));
    }

    integrationWebhookError(xhr: any, fallback: string) {
        const error = xhr.responseJSON && xhr.responseJSON.error;
        showIntegrationToast(error ? `${fallback} ${error}` : fallback, "error");
    }

    addIntegrationWebhook(button: JQuery<HTMLElement>) {
        const content = $("#integration-webhooks-modal .modal-content");
        const select = content.find("#integration-webhook-event");
        const event = String(select.val() || "");
        if (!event || button.prop("disabled")) return;
        const label = button.find("span").text();
        button.prop("disabled", true).find("span").text(String(button.data("busy-label")));
        $.ajax({
            url: content.data("url"),
            method: "POST",
            dataType: "json",
            headers: { "X-CSRF-Token": $('meta[name="csrf-token"]').attr("content") },
            data: { [String(content.data("event-param"))]: event }
        })
            .done(() => this.reloadIntegrationWebhooks(`“${select.find("option:selected").text()}” webhook added.`))
            .fail((xhr: any) => {
                this.integrationWebhookError(xhr, "Couldn't add the webhook.");
                button.prop("disabled", false).find("span").text(label);
            });
    }

    deleteIntegrationWebhook(button: JQuery<HTMLElement>) {
        const content = $("#integration-webhooks-modal .modal-content");
        const name = $("<div>").text(String(button.data("name") || "")).html();
        if (button.prop("disabled")) return;
        snConfirm({
            title: "Delete this webhook?",
            message: `<b>${name}</b> will be deleted, and Senitron stops receiving these events.`,
            confirmText: "Delete"
        }).then((confirmed) => {
            if (!confirmed) return;
            button.prop("disabled", true);
            $.ajax({
                url: `${content.data("url")}/${encodeURIComponent(String(button.attr("data-id")))}`,
                method: "DELETE",
                dataType: "json",
                headers: { "X-CSRF-Token": $('meta[name="csrf-token"]').attr("content") }
            })
                .done(() => this.reloadIntegrationWebhooks(`“${button.data("name")}” webhook deleted.`))
                .fail((xhr: any) => {
                    this.integrationWebhookError(xhr, "Couldn't delete the webhook.");
                    button.prop("disabled", false);
                });
        });
    }

    setCatalogDownloadButton(btnEl: JQuery<HTMLElement>, connect: boolean) {
        const wrapper = $("#download-full-catalog-btn");
        const button = wrapper.find("button");
        const url = connect ? "" : String(btnEl.attr("data-catalog-download-url") || "");
        const running = btnEl.attr("data-catalog-download-running") === "true";
        wrapper.toggle(!!url).attr("title", running ? String(wrapper.data("running-title")) : null);
        button.attr("data-url", url).data("url", url).prop("disabled", !url || running || button.data("can-download") !== true);
    }

    setConnectMode(btnEl: JQuery<HTMLElement>) {
        const connect = btnEl.data("connect") === true;
        const form = $("#editIntegration .modal-content > form");
        const save = form.find(".btn-save");
        form.find("[data-connect-skipped]").prop("disabled", false).removeAttr("data-connect-skipped");
        form.find("input[name=_method]").val(connect ? "post" : "patch");
        form.find("input.connect-name").remove();
        if (connect) form.append($("<input>", { type: "hidden", class: "connect-name", name: "name", value: String(btnEl.data("title")) }));
        $("#editIntegration .dlg-connect-word").toggle(connect);
        $("#editIntegration .dlg-settings-word").toggle(!connect);
        this.setCatalogDownloadButton(btnEl, connect);
        $("#editIntegration .twilio-footer-actions").toggle(btnEl.data("title") === "twilio");
        $("#editIntegration .keonn-footer-actions").toggle(btnEl.data("title") === "keonn");
        $("#editIntegration .keonn-test-results").prop("hidden", true).find(".test-lines").empty();
        const d365Actions = $("#editIntegration .d365-footer-actions");
        const showD365 = btnEl.data("title") === "d365";
        d365Actions.toggle(showD365);
        const advanced = d365Actions.find(".d365-advanced-link");
        advanced.toggleClass("disabled", connect).attr("aria-disabled", connect ? "true" : null).attr("title", connect ? String(advanced.data("connect-title")) : null);
        if (showD365 && connect) {
            advanced.removeAttr("data-url").removeData("url");
            const testUrl = String(d365Actions.data("connect-test-url"));
            d365Actions.find(".d365-test-link").attr("data-url", testUrl).data("url", testUrl);
        } else if (showD365) {
            const id = encodeURIComponent(String(btnEl.data("id")));
            const advancedUrl = String(d365Actions.data("advanced-url")).replace("__ID__", id);
            advanced.attr("data-url", advancedUrl).data("url", advancedUrl);
            const testUrl = String(d365Actions.data("test-url")).replace("__ID__", id);
            d365Actions.find(".d365-test-link").attr("data-url", testUrl).data("url", testUrl);
        }
        $(".qb-sync-invoices").toggle(!connect);
        const webhooksToggle = $(".connect_shopify_webhooks_field");
        webhooksToggle.toggle(connect && btnEl.data("title") === "shopify").find(":input").prop("disabled", !(connect && btnEl.data("title") === "shopify"));
        webhooksToggle.find("input:checkbox").prop("checked", true);
        save.text(save.data(connect ? "connect-label" : "save-label"));

        this.connecting = connect;
        form.find("[data-connect-required]").prop("required", false).removeAttr("data-connect-required");
        form.find("[data-settings-required]").removeAttr("data-settings-required");
        form.find(".form-group.is-required").removeClass("is-required");
        (REQUIRED_FIELDS[String(btnEl.data("title"))] || []).forEach((name) => {
            form.find(`[name="${name}"]`).not("[type=hidden]").each((_, el) => {
                const category = $(el).closest(".int-cat")[0];
                if (!category || !this.isShownWithin(el, category)) return;
                $(el).closest(".form-group").addClass("is-required");
                if (connect) $(el).prop("required", true).attr("data-connect-required", "");
                else $(el).attr("data-settings-required", "");
            });
        });
    }

    validateSettingsForm(e: any) {
        if (e.isDefaultPrevented()) return;
        const form = $(e.currentTarget);
        const invalid = form.find(":input[required]").filter((_, el) => !(el as HTMLInputElement).checkValidity()).first();
        if (invalid.length) {
            e.preventDefault();
            $("#int-search").val("");
            this.selectCategory(String(invalid.closest(".int-cat").data("cat")));
            (invalid[0] as HTMLInputElement).reportValidity();
            return;
        }
        if (this.connecting) this.skipHiddenFields(form);
        form.find(".btn-save").prop("disabled", true);
    }

    skipHiddenFields(form: JQuery<HTMLElement>) {
        form.find(".int-cat :input[name]:not(:disabled)").each((_, el) => {
            const category = $(el).closest(".int-cat")[0];
            let hidden = false;
            for (let node = el.parentElement; node && node !== category; node = node.parentElement) {
                if (!node.classList.contains("search-miss") && getComputedStyle(node).display === "none") hidden = true;
            }
            if (!hidden) return;
            $(el).prop("disabled", true).attr("data-connect-skipped", "");
        });
    }

    fillDialogHeader(btnEl: JQuery<HTMLElement>) {
        const name = String(btnEl.data("integration-display-name") || "");
        const logoSrc = String(btnEl.data("logo") || "");
        const logo = $("#dlg-logo").empty();
        if (logoSrc) {
            logo.append($("<img>", { src: logoSrc, alt: `${name} logo` }));
        } else {
            logo.append($("<div>", { class: "mono-badge", role: "img", "aria-label": `${name} logo`, text: name.slice(0, 2).toUpperCase() }));
        }
        $("#dlg-sub").text(String(btnEl.data("subtitle") || ""));
        $("#editIntegration .secret .fld").addClass("masked");
        $("#editIntegration [data-reveal]").text("Show");
    }


    isShownWithin(el: Element, root: Element) {
        for (let node: Element | null = el; node && node !== root; node = node.parentElement) {
            if (getComputedStyle(node).display === "none") return false;
        }
        return true;
    }

    visibleOptions(category: Element) {
        return $(category).find(".form-group").filter((_, el) => this.isShownWithin(el, category));
    }

    availableCategories() {
        return $("#editIntegration .int-cat").toArray().filter((category) => this.visibleOptions(category).length > 0);
    }

    refreshCategories() {
        const available = this.availableCategories().map((category) => String($(category).data("cat")));
        $("#editIntegration .cat-btn").each((_, btn) => {
            $(btn).toggle(available.indexOf(String($(btn).data("cat"))) !== -1);
        });
        this.updateCategoryBadges();
        if (String($("#int-search").val() || "").trim()) {
            this.searchSettings();
        } else if (!this.currentCategory || available.indexOf(this.currentCategory) === -1) {
            this.selectCategory(available[0] || null);
        }
    }

    updateCategoryBadges() {
        $("#editIntegration .int-cat").each((_, category) => {
            const options = this.visibleOptions(category);
            const switches = options.find("input.sw[type=checkbox]");
            const label = switches.length
                ? `${switches.filter(":checked").length}/${switches.length}`
                : `${options.length} ${options.length === 1 ? "field" : "fields"}`;
            $(`#editIntegration .cat-btn[data-cat="${$(category).data("cat")}"] .cat-badge`).text(options.length ? label : "");
        });
    }

    selectCategory(id: string | null) {
        this.currentCategory = id;
        $("#editIntegration .form-group").removeClass("search-miss");
        $("#editIntegration .int-search-empty").hide();
        $("#editIntegration .int-cat").each((_, category) => {
            $(category).toggle(String($(category).data("cat")) === id);
        });
        $("#editIntegration .cat-btn").each((_, btn) => {
            const on = String($(btn).data("cat")) === id;
            $(btn).toggleClass("on", on).attr("aria-current", on ? "page" : null);
        });
    }

    searchSettings() {
        const query = String($("#int-search").val() || "").trim().toLowerCase();
        if (!query) {
            this.selectCategory(this.currentCategory);
            return;
        }
        $("#editIntegration .cat-btn").removeClass("on").removeAttr("aria-current");
        $("#editIntegration .form-group").removeClass("search-miss");
        $("#editIntegration .int-cat").hide();
        let matches = 0;
        this.availableCategories().forEach((category) => {
            const options = this.visibleOptions(category);
            const heading = $(category).children("h3, p").text().toLowerCase();
            options.each((_, option) => {
                if (heading.indexOf(query) === -1 && $(option).text().toLowerCase().indexOf(query) === -1) $(option).addClass("search-miss");
            });
            const found = options.not(".search-miss").length;
            matches += found;
            $(category).toggle(found > 0);
        });
        $("#editIntegration .int-search-empty").toggle(matches === 0);
    }

    fieldStates() {
        const states = new Map<Element | string, string>();
        $("#editIntegration .modal-content > form").find("input, select, textarea").not("[type=hidden], #int-search").each((_, el) => {
            const input = el as HTMLInputElement;
            if (input.type === "radio") {
                const key = `radio:${input.name}`;
                if (!states.has(key)) states.set(key, "");
                if (input.checked) states.set(key, input.value);
            } else if (input.type === "checkbox") {
                states.set(el, String(input.checked));
            } else {
                states.set(el, String($(el).val() ?? ""));
            }
        });
        return states;
    }

    snapshotValues() {
        this.initialValues = this.fieldStates();
        this.updateUnsavedStatus();
    }

    updateUnsavedStatus() {
        this.updateCopyButtons();
        let changed = 0;
        this.fieldStates().forEach((value, key) => {
            if (this.initialValues.get(key) !== value) changed++;
        });
        const form = $("#editIntegration .modal-content > form");
        const missing = form.find("[data-connect-required]").filter((_, el) => !(el as HTMLInputElement).disabled && String($(el).val() ?? "").trim() === "");

        if (this.connecting) {
            $("#int-status")
                .text(missing.length ? `${missing.length} required field${missing.length === 1 ? "" : "s"} left` : "Ready to connect")
                .toggleClass("dirty", missing.length > 0);
            form.find(".btn-save").prop("disabled", missing.length > 0);
            return;
        }

        const emptied = form.find("[data-settings-required]").filter((_, el) =>
            !(el as HTMLInputElement).disabled && String($(el).val() ?? "").trim() === "" && String(this.initialValues.get(el) ?? "").trim() !== "");
        if (emptied.length) {
            $("#int-status").text(`${emptied.length} required field${emptied.length === 1 ? " is" : "s are"} empty`).addClass("dirty");
            form.find(".btn-save").prop("disabled", true);
            return;
        }

        $("#int-status")
            .text(changed ? `${changed} unsaved change${changed === 1 ? "" : "s"}` : "No changes")
            .toggleClass("dirty", changed > 0);
        form.find(".btn-save").prop("disabled", changed === 0);
    }

    // openPopUpWindow(url) {
    //     // Launch Popup
    //     const width = 800;
    //     const height = 650;
    //
    //     let top = window.screen.height - height;
    //     top = top > 0 ? top/2 : 0;
    //
    //     let left = window.screen.width - width;
    //     left = left > 0 ? left/2 : 0;
    //
    //     const parameters = "location=1,width=" + width + ",height=" + height + ",top=" + top + ",left=" + left;
    //     const win = window.open(url,"Connect to QuickBooks", parameters);
    //     win.moveTo(left, top);
    //     win.focus();
    // }

    onSelectEditDialog = async (el: HTMLElement) => {
        const btnEl = $(el);
        this.hideAllElements();
        if(btnEl.data("title") != "quickbook")
            this.showGeneralFields();
        if (btnEl.data("title") == "auditlogs") {
            this.showFormOfPostLog();
        } else if (btnEl.data("title") == "treez") {
            this.showFormOfTreez();
        } else if (btnEl.data("title") == "odoo") {
            this.showFormOfOdoo();
        } else if (btnEl.data("title") == "heartland") {
            this.showFormOfHeartland();
        } else if (btnEl.data("title") == "vend") {
            this.showFormOfVend();
        } else if (btnEl.data("title") == "shopify") {
            this.showFormOfShopify();
        } else if (btnEl.data("title") == "lightspeed") {
            this.showCredentialFields();
            this.showFormOfLightspeed();
        } else if (btnEl.data("title") == "ls_pos") {
            this.showFormOfLightspeedPos();
        } else if (btnEl.data('title') === 'twilio') {
            this.showFormOfTwilio();
        } else if (btnEl.data("title") == "quickbook") {
            this.showFormOfQuickBook(btnEl.data("connect") === true);
        } else if (btnEl.data("title") == "quickbooks_desktop") {
            this.showFormOfQuickBooksDesktop();
        } else if (btnEl.data("title") == "netsuite") {
            this.showFormOfNetsuite();
        } else if (btnEl.data("title") == "retail_express") {
            this.showFormOfRetailExpress();
        } else if (btnEl.data("title") == "microsoft_azure") {
            this.showFormOfMicrosoftAzure();
        } else if (btnEl.data("title") == "acumatica") {
            this.showFormOfAcumatica();
        } else if (btnEl.data("title") == "d365") {
            this.showFormOfD365();
        } else if (btnEl.data("title") == "custom_d365") {
            this.showFormOfCustomD365();
        } else if (btnEl.data("title") == "retail_pro_prism") {
            this.showFormOfRetailProPrism();
        } else if (btnEl.data("title") == "keonn") {
            this.showFormOfKeonn();
        } else if (btnEl.data("title") == "redo") {
            this.showFormOfRedo();
        } else {
            this.showCredentialFields();
            $(".port_field").show();
        }
        this.onEditIntegration(el);
    }

    onEditIntegration = async (el: HTMLElement) => {
        const btnEl = $(el);
        $("#username").val(btnEl.data("username"));
        $(".integration-name").text(btnEl.data("integration-display-name"));
        $("#password").val(btnEl.data("password"));
        $("#url").val(btnEl.data("url"));
        $("#port").val(btnEl.data("port"));
        $("#api_key").val(btnEl.data("api_key"));
        $("#api_secret_key").val(btnEl.data("api_secret_key"));
        $("#dispensary_name").val(btnEl.data("dispensary_name"));
        $("#ext_client_id").val(btnEl.data("ext_client_id"));
        $("#account_id").val(btnEl.data("account_id"));
        $("#ext_client_secret").val(btnEl.data("ext_client_secret"));
        $("#netsuite_full_catalog_script").val(btnEl.data("netsuite_full_catalog_script"));
        $("#netsuite_catalog_script").val(btnEl.data("netsuite_catalog_script"));
        $("#license").val(btnEl.data("license"));
        $("#error_email").val(btnEl.data("error_email"));
        $("#token").val(btnEl.data("token"));
        $("#queue_name").val(btnEl.data("queue_name"));
        $("#domain_prefix").val(btnEl.data("domain-prefix"));
        $("#store_name").text(btnEl.data("store_name"));
        $("#database").val(btnEl.data("database"));
        $("#company").val(btnEl.data("company"));
        $("input:checkbox[name=enable_crm_functionalities]").prop("checked", btnEl.data("enable_crm_functionalities"));
        $("input:checkbox[name=enabled]").prop("checked", btnEl.data("enabled"));


        $("input:checkbox[name=ls_enable_automatic_sync]").prop("checked", btnEl.data("lsenableautomaticsync") == true);
        $("#allow_reconciliation").prop("checked", btnEl.data("allow_reconciliation") === true);
        $("input:checkbox[name=enable_sync_to_netsuite]").prop("checked", btnEl.data("enable_sync_to_netsuite") === true);

        $("#editIntegration").data("gs1-integration", String(btnEl.data("integration-name") || ""));
        if (btnEl.data("integration-name") == 'Ls POS') {
            $("#gs_one_standard_field").hide();
            $(".disable_upc_validation").hide();
        }

        if (btnEl.data("integration-name") == 'Lightspeed' || btnEl.data("integration-name") == 'Shopify' || btnEl.data("integration-name") == 'Vend' || btnEl.data("integration-name") == 'Netsuite' || btnEl.data("integration-name") == 'Odoo') {
                $(".gs_one_standard_field").show();
                $("#gs_one_standard_field").show();
                if (btnEl.data("integration-name") == 'Odoo' || btnEl.data("integration-name") == 'Netsuite') {
                    this.setGs1FieldOptions([['UPC', 'upc']], btnEl.data("gs_one_standard_field") || 'upc');
                } else {
                    const gs1Select = $("#gs_one_standard_field");
                    const saved = String(btnEl.data("gs_one_standard_field") || "");
                    const hasSaved = gs1Select.find("option").filter((_, option) => (option as HTMLOptionElement).value === saved).length > 0;
                    gs1Select.val(hasSaved ? saved : gs1Select.find("option").first().val()).trigger("change.select2");
                }
        }

        $("input:checkbox[name=ls_enable_transfer_slip_commit]").prop("checked", btnEl.data("lsenabletransferslipcommit"));
        $("input:checkbox[name=ls_enable_for_download_all_assets]").prop("checked", btnEl.data("lsenablefordownloadallassets"));
        $("input:checkbox[name=ls_allow_sn_negative_qty]").prop("checked", btnEl.data("lsallowsnnegativeqty"));
        $("input:checkbox[name=gs_one_standard]").prop("checked", btnEl.data("gs_one_standard"));
        this.updateGs1Validation(false);
        $("input:checkbox[name=ls_enable_sync_custom_fields_automatically]").prop("checked", btnEl.data("lsenablesynccustomfieldsautomatically"));
        $("input:checkbox[name=qb_enable_auto_polling_invoices]").prop("checked", btnEl.data("qb_enable_auto_polling_invoices"));
        $("input:checkbox[name=enable_transfer_orders_functionalities]").prop("checked", btnEl.data("enable_transfer_orders_functionalities"));
        $("input:checkbox[name=enable_automatic_pulling_of_transfer_orders]").prop("checked", btnEl.data("lsenablepulltransferordersautomatically"));
        $("input:checkbox[name=enable_item_fulfillment_functionality_for_transfer_orders]").prop("checked", btnEl.data("enable_item_fulfillment_functionality_for_transfer_orders"));
        $("input:checkbox[name=enable_eos_inventory_quantities_importer]").prop("checked", btnEl.data("lsenablepulleosinventoryquantitiesimporter"));
        $("input:checkbox[name=enable_automatic_pulling_of_purchase_orders]").prop("checked", btnEl.data("lsenablepullpurchaseordersautomatically"));
        $("input:checkbox[name=enable_automatic_pulling_of_sales_orders]").prop("checked", btnEl.data("lsenablepullsalesordersautomatically"));
        $("input:checkbox[name=enable_automatic_pulling_of_return_to_vendor_orders]").prop("checked", btnEl.data("lsenablepullreturntovendorordersautomatically"));
        $("input:checkbox[name=enable_multi_location_return_to_vendor_orders]").prop("checked", btnEl.data("enable_multi_location_return_to_vendor_orders"));
        $("input:checkbox[name=enable_webhook_for_sales_orders]").prop("checked", btnEl.data("lsenablewebhookforsalesorders"));
        $("input:checkbox[name=enable_webhook_for_purchase_orders]").prop("checked", btnEl.data("lsenablewebhookforpurchaseorders"));
        $("input:checkbox[name=enable_sync_purchase_order_receiving_items_to_netsuite]").prop("checked", btnEl.data("ls_enable_sync_purchase_order_receiving_items_to_netsuite"));
        $("input:checkbox[name=enable_6laps_item_filtering]").prop("checked", btnEl.data("enable_6laps_item_filtering"));
        $("input:checkbox[name=enable_pull_draft_items]").prop("checked", btnEl.data("enable_pull_draft_items"));
        $("input:checkbox[name=shopify_use_product_type_as_category]").prop("checked", btnEl.data("shopify_use_product_type_as_category"));
        $("input:checkbox[name=pull_shipment_data]").prop("checked", btnEl.data("pull_shipment_data") === true);
        $("input:checkbox[name=enable_concurrent_picking_with_shipment]").prop("checked", btnEl.data("enable_concurrent_picking_with_shipment") === true);
        // runs toggleConcurrentPicking (_edit.html.erb) so the picking option follows the loaded state
        $("input:checkbox[name=pull_shipment_data]").trigger("change");
        $("input:checkbox[name=pull_picking_data]").prop("checked", btnEl.data("pull_picking_data") === true);

        if (btnEl.data("properties") !== 'null' &&
            btnEl.data("properties") !== null &&
            Object.keys(btnEl.data("properties")).length != 0) {
            const integrationName = btnEl.data("title").toLowerCase();
            const properties = btnEl.data("properties");
            if (properties) {
                switch(integrationName) {
                    case 'redo':
                        $("#properties_redo_store_id").val(properties.store_id);
                        break;

                    case 'twilio':
                        Object.keys(properties).forEach(function (item) {
                            $(`input[id=properties_${integrationName}_${item}]`).val(properties[item]);
                        });
                        break;

                    case 'keonn':
                        Object.keys(properties).forEach(function (item) {
                            const input = $(`input[id=properties_keonn_${item}]`);
                            if (input.is(":checkbox")) input.prop("checked", String(properties[item]) === "true");
                            else input.val(properties[item]);
                        });
                        this.toggleMovementEosFields();
                        break;

                    case 'lightspeed':
                        Object.keys(properties).forEach(function (item) {
                            const checked = (String(properties[item]).toLowerCase() === 'true');
                            $(`input:checkbox[id=properties_${integrationName}_${item}]`).prop("checked", checked);
                        });
                        break;

                    default:
                        break;
                }
            }
        }

        if (btnEl.data("title") === "keonn") {
            if (btnEl.data("connect") === true) {
                if (!$("#url").val()) $("#url").val("kafka.senitron.net");
                if (!$("#port").val()) $("#port").val("9093");
            }
            const token = $("#properties_keonn_workspace_token");
            token.data("previous", String(token.val() || "").trim().toLowerCase());
        }

        $("#ls_automatic_sync_interval").val(btnEl.data("lsautomaticsyncinterval"));

        // show/hide shopify webhook configs
        const intgName = btnEl.data("title").toLowerCase();
        this.showShopifyWebhookConfigs(intgName);
        $('input[type="checkbox"][id="enabled"]').on("change", () => {
          this.showShopifyWebhookConfigs(intgName);
        });

        // the item fulfillment option only makes sense on top of one of the transfer order options
        const transferOrderCheckboxes = "input:checkbox[name=enable_transfer_orders_functionalities], input:checkbox[name=enable_automatic_pulling_of_transfer_orders]";
        this.showItemFulfillmentFunctionality(intgName);
        $(transferOrderCheckboxes).off("change.itemFulfillment").on("change.itemFulfillment", () => {
          this.showItemFulfillmentFunctionality(intgName);
        });

        // the multi-location option only makes sense on top of the return to vendor option
        const returnToVendorCheckbox = "input:checkbox[name=enable_automatic_pulling_of_return_to_vendor_orders]";
        this.toggleMultiLocationReturnToVendorFunctionality(intgName);
        $(returnToVendorCheckbox).off("change.multiLocationRtv").on("change.multiLocationRtv", () => {
          this.toggleMultiLocationReturnToVendorFunctionality(intgName);
        });

        $("#editIntegration .modal-content > form").prop("action", btnEl.data("updateurl"));
        this.setConnectMode(btnEl);

        this.fillDialogHeader(btnEl);
        this.currentCategory = null;
        $("#int-search").val("");
        this.enhanceSelects();
        this.refreshCategories();
        $(".shopify-webhook-configs").promise().done(() => this.refreshCategories());
        this.snapshotValues();

        $("#editIntegration").modal("show");
    }

    showItemFulfillmentFunctionality(integrationName) {
      const field = $('.enable_item_fulfillment_functionality_for_transfer_orders_field');
      const transferOrdersEnabled = $("input:checkbox[name=enable_transfer_orders_functionalities]").is(":checked") ||
        $("input:checkbox[name=enable_automatic_pulling_of_transfer_orders]").is(":checked");

      if (integrationName == "netsuite" && transferOrdersEnabled) {
        field.show();
      } else {
        // uncheck it as well, so it can never stay on without one of the transfer order options
        $("input:checkbox[name=enable_item_fulfillment_functionality_for_transfer_orders]").prop("checked", false);
        field.hide();
      }
    }

    // The option stays on the form for netsuite whether or not the return to
    // vendor option is on, greyed out while it is off, so it is clear it exists
    // and what has to be turned on first.
    toggleMultiLocationReturnToVendorFunctionality(integrationName) {
      const field = $('.enable_multi_location_return_to_vendor_orders_field');
      const checkbox = $("input:checkbox[name=enable_multi_location_return_to_vendor_orders]");
      const returnToVendorEnabled = $("input:checkbox[name=enable_automatic_pulling_of_return_to_vendor_orders]").is(":checked");

      if (integrationName != "netsuite") {
        checkbox.prop("checked", false).prop("disabled", true);
        field.hide().addClass("option-requires-another");
        return;
      }

      field.show();

      if (returnToVendorEnabled) {
        checkbox.prop("disabled", false);
        field.removeClass("option-requires-another");
      } else {
        // uncheck it as well, so it can never stay on without the return to
        // vendor option. Only the checkbox is disabled - its hidden field still
        // has to submit the false that turns the option off.
        checkbox.prop("checked", false).prop("disabled", true);
        field.addClass("option-requires-another");
      }
    }

    showShopifyWebhookConfigs(integrationName) {
      if (integrationName == "shopify" && $('input[type="checkbox"][id="enabled"]').is(":checked")) {
        $(".shopify-webhook-configs").fadeIn();
      } else {
        $(".shopify-webhook-configs").fadeOut();
      }
    }

    // Between bad and worst, we don't have more choice for structure, just a few improvements
    showFormOfTreez() {
        $(".api_key_field").show();
        $(".username_field").show();
        $(".password_field").show();
        $(".dispensary_name_field").show();
        $(".ext_client_id_field").show();
        $(".ext_client_secret_field").insertAfter(".ext_client_id_field").show();
        $(".api_secret_key_field").insertAfter(".ext_client_secret_field").show();
        $(".account_id_field").show();
        $(".license_field").show();
        $(".url_field").hide();
        $(".token_field").hide();
        $("#api_secret_key_label").text("Private Key");
        $("#ext_client_secret_label").text("Certificate ID");
        $("#account_id_label").text("Organization ID");
        $("#api_secret_key").attr("rows", "8");
    }

    showFormOfNetsuite() {
        $(".api_key_field").show();
        $(".api_secret_key_field").show();
        $(".ext_client_id_field").show();
        $(".ext_client_secret_field").show();
        $(".domain_prefix_field").show();
        $(".enabled_field").show();
        $(".ls_enable_automatic_sync_field").show();
        $(".ls_enable_transfer_slip_commit_field").show();
        $(".ls_enable_sync_custom_fields_automatically_field").show();
        $(".ls_automatic_sync_interval_field").show();
        $(".ls_enable_not_existed_items_field").show();
        $(".enable_follow_up_email_field").show();
        $(".ls_allow_sn_negative_qty_field").show();
        $(".download-full-catalog-btn").show();
        $(".url_field").hide();
        $(".gs_one_standard_field").show();
        this.setGs1FieldOptions([['UPC', 'upc']]);
        $(".netsuite_full_catalog_script_field").show();
        $(".netsuite_catalog_script_field").show();
        $(".enable_sync_to_netsuite_field").show();
        $(".enable_creating_missing_catalog_items_field").show();
        $(".netsuite_qty_download_mode_field").show().find(":input").prop("disabled", false);
        $('.enable_transfer_orders_functionalities').show();
        $('.enable_eos_inventory_quantities_importer_field').show();
        $('.enable_automatic_pulling_of_transfer_orders_field').show();
        $('.enable_automatic_pulling_of_purchase_orders_netsuite_field').show().find(':input').prop('disabled', false);
        $('.enable_automatic_pulling_of_sales_orders_field').show();
        $('.enable_automatic_pulling_of_return_to_vendor_orders_field').show();
        $('.enable_webhook_for_sales_orders_field').show();
        $('.enable_webhook_for_purchase_orders_field').show();
        $('.enable_sync_purchase_order_receiving_items_to_netsuite_field').show();
        $('.enable_6laps_item_filtering_field').show();
    }

    showFormOfRetailExpress() {
        $(".api_key_field").show();
        $(".enabled_field").show();
        $(".download-full-catalog-btn").show();
        $(".url_field").hide();
        $(".disable_upc_validation").hide();
        $(".gs_one_standard_text_field").hide();
        $(".account_id_field").hide();
        $(".ls_enable_transfer_slip_commit_field").show()
        $(".ls_enable_not_existed_items_field").show();
        $(".enable_follow_up_email_field").show();
        $(".ls_allow_sn_negative_qty_field").show();
        $(".domain_prefix_field").show();
        $(".username_field").show();
        $(".password_field").show();
        $(".ext_client_id_field").show();
    }

    showFormOfQuickBook(connect: boolean) {
        $(".enabled_field").show();
        $(".qb_enable_auto_polling_invoices").show();
        $(".qb_about_field").show();
        $(".account_id_field").toggle(!connect);
        if (connect) {
            $(".ext_client_id_field").show();
            $(".api_secret_key_field").show();
            $("#api_secret_key_label").text("Client Secret");
            $(".account_id_field").hide();
        }
    }

    showFormOfQuickBooksDesktop() {
      $(".account_id_field").hide();
      $(".port_field").show();
      $(".api_key_field").show();
      $('.enable_crm_functionalities').show();
    }

    showFormOfMicrosoftAzure() {
        $(".url_field").hide();
        $(".domain_prefix_field").show();
        $(".token_field").show();
        $(".queue_name_field").show();
    }

    showFormOfAcumatica() {
        $(".url_field").hide();
        $(".domain_prefix_field").show();
        $(".username_field").show();
        $(".password_field").show();
        $(".account_id_field").show();
        $(".ext_client_id_field").show();
        $(".enable_follow_up_email_field").show();
        $(".acumatica-sync-by-adjustment-field").show();
        $('.gs_one_standard_text_field').hide();
        $(".download-full-catalog-btn").show();
        $(".pull-shipment-data-field").show();
        $(".acumatica-pull-shipment-data-text").show();
        $('.acumatica-enable-concurrent-picking-with-shipment').show();
    }

    showFormOfD365() {
      $(".account_id_field").hide();
      $(".port_field").show();
      $(".username_field").show();
      $(".password_field").show();
      $(".enabled_field").show();
      $(".company_field").show();
      $(".d365_fields").show().find(":input").prop("disabled", false);
      $(".download-full-catalog-btn").show();
      $(".ls_enable_transfer_slip_commit_field").show();
    }

    showFormOfCustomD365() {
      $(".url_field").hide();
      $(".username_field").hide();
      $(".password_field").hide();
      $(".gs_one_standard_field").hide();
      $(".gs_one_standard_text_field").hide();
      $(".enabled_field").show();
      $(".enable_follow_up_email_field").show();
      $(".account_id_field").show();
      $(".domain_prefix_field").show();
      $(".ext_client_id_field").show();
      $(".ext_client_secret_field").show();
      $(".scope_field").show();
      $(".enable_creating_missing_catalog_items_field").show();
      $(".company_field").show();
      $(".download-full-catalog-btn").show();
      $('.enable_transfer_orders_functionalities').show();
    }

    showFormOfRetailProPrism() {
      $(".url_field").hide();
      $(".username_field").show();
      $(".password_field").show();
      $(".account_id_field").hide();
      $(".gs_one_standard_text_field").hide();
    }

    showFormOfKeonn() {
        $(".keonn_fields").show().find(":input").prop("disabled", false).filter("input").not(":checkbox, [type=hidden]").val("");
        $("#properties_keonn_trigger_instant_api").prop("checked", false);
        $("#properties_keonn_send_movement_notifications_to_eos").prop("checked", false);
        this.toggleMovementEosFields();
        $(".url_field").insertBefore(".username_field").show();
        $(".port_field").insertAfter(".url_field").show();
        $(".username_field").show();
        $(".password_field").show();
        $("#url_label").text("Kafka server");
        this.setConnectionHeading("Kafka server", "Where Keonn streams tag positions (x/y).");
    }

    showFormOfRedo() {
        $(".url_field").hide();
        $(".token_field").show();
        $(".redo_fields").show().find(":input").prop("disabled", false);
        $(".redo_fields input[type=text]").val("");
        $("#token_label").text("API token");
        this.setConnectionHeading("Redo API", "Create the token in Redo > Settings > Developer, with Orders read and write.");
    }

    setConnectionHeading(title: string | null, description: string | null) {
        const parts = [
            $('#editIntegration .cat-btn[data-cat="connection"] span').first(),
            $('#editIntegration .int-cat[data-cat="connection"] > h3'),
            $('#editIntegration .int-cat[data-cat="connection"] > p')
        ];
        parts.forEach((el) => {
            if (el.data("default-text") === undefined) el.data("default-text", el.text());
        });
        parts[0].text(title ?? parts[0].data("default-text"));
        parts[1].text(title ?? parts[1].data("default-text"));
        parts[2].text(description ?? parts[2].data("default-text"));
    }

    toggleMovementEosFields() {
        $("#editIntegration .movement_eos_fields").toggle($("#properties_keonn_send_movement_notifications_to_eos").is(":checked"));
    }

    keonnSubdomain(value: string) {
        return value.trim().replace(/^[a-z]+:\/\//i, "").replace(/\/.*$/, "").replace(/\.keonn\.com$/i, "").toLowerCase();
    }

    testKeonnConnection(button: JQuery<HTMLElement>) {
        if (button.prop("disabled")) return;
        const form = $("#editIntegration .modal-content > form");
        const data: { [name: string]: string } = {};
        ["url", "port", "username", "password", "properties[keonn][workspace_token]", "properties[keonn][portal_subdomain]",
         "properties[keonn][api_username]", "properties[keonn][api_password]", "properties[keonn][kafka_topic]",
         "properties[keonn][kafka_snapshot_topic]"].forEach((name) => {
            data[name] = String(form.find(`[name="${name}"]`).not("[type=hidden]").first().val() || "");
        });
        button.prop("disabled", true).text(String(button.data("busy-label")));
        this.renderKeonnTest({ portal: "running", kafka: "waiting" });
        const failedWith = (message: string) => this.renderKeonnTest({ portal: { ok: false, message }, kafka: { ok: false, message } });
        $.ajax({
            url: String(button.data("url")),
            method: "POST",
            data,
            dataType: "json",
            headers: { "X-CSRF-Token": String($('meta[name="csrf-token"]').attr("content") || "") }
        }).done((response) => {
            if (response && response.portal && response.kafka) this.renderKeonnTest(response);
            else failedWith("Couldn't run the test. Please try again.");
        }).fail((xhr) => {
            failedWith((xhr.responseJSON && xhr.responseJSON.error) || "Couldn't run the test. Please try again.");
        }).always(() => {
            button.prop("disabled", false).text(String(button.data("label")));
        });
    }

    renderKeonnTest(result: { [key: string]: any }) {
        const lines = [["portal", "Keonn portal"], ["kafka", "Kafka server"]].map(([key, label]) => {
            const state = result[key];
            const text = $("<span>").append($("<b>").text(`${label}:`));
            const line = $("<div>", { class: "test-line" });
            if (state === "running") return line.append($("<span>", { class: "int-spinner sm" }), text.append(" Testing…"));
            if (state === "waiting") return line.append(text.append(" Waiting…"));
            return line.addClass(state.ok ? "ok" : "fail").append(text.append(` ${state.message}`));
        });
        const box = $("#editIntegration .keonn-test-results").prop("hidden", false);
        box.find(".test-lines").empty().append(lines);
        box.find(".test-close").toggle(result.portal !== "running");
    }

    syncKeonnSubdomain(token: JQuery<HTMLElement>) {
        const subdomain = $("#properties_keonn_portal_subdomain");
        const value = String(token.val() || "").trim().toLowerCase();
        const current = String(subdomain.val() || "");
        if (!current || current === token.data("previous")) subdomain.val(value);
        token.data("previous", value);
    }

    showFormOfOdoo() {
        $(".username_field").show();
        $(".password_field").show();
        $(".url_field").show();
        $(".database_field").show();
        $(".ls_enable_automatic_sync_field").show();
        $(".ls_enable_transfer_slip_commit_field").show();
        $(".ls_enable_sync_custom_fields_automatically_field").show();
        $(".ls_automatic_sync_interval_field").show();
        $(".ls_enable_not_existed_items_field").show();
        $(".enable_follow_up_email_field").show();
        $(".ls_allow_sn_negative_qty_field").show();
        $(".download-full-catalog-btn").show();
        $(".gs_one_standard_field").show();
        this.setGs1FieldOptions([['UPC', 'upc']], 'upc');
        $(".pull-shipment-data-field").show();
        $(".odoo-pull-shipment-data-text").show();
        $(".odoo-pull-picking-data-field").show();
    }

    showFormOfHeartland() {
        $(".domain_prefix_field").show();
        $(".token_field").show();
        $(".url_field").hide();
        $(".ls_enable_automatic_sync_field").show();
        $(".ls_enable_transfer_slip_commit_field").show();
        $(".ls_enable_sync_custom_fields_automatically_field").show();
        $(".disable_upc_validation").hide();
        $(".gs_one_standard_text_field").hide();
        $(".ls_automatic_sync_interval_field").show();
        $(".ls_enable_not_existed_items_field").show();
        $(".enable_follow_up_email_field").show();
        $(".ls_allow_sn_negative_qty_field").show();
        $(".download-full-catalog-btn").show();
    }

    showFormOfVend() {
        $(".domain_prefix_field").show();
        $(".token_field").show();
        $(".account_id_field").hide();
        $(".url_field").hide();
        $(".ls_enable_automatic_sync_field").show();
        $(".ls_enable_transfer_slip_commit_field").show();
        $(".ls_enable_sync_custom_fields_automatically_field").show();
        $(".ls_automatic_sync_interval_field").show();
        $(".ls_enable_not_existed_items_field").show();
        $(".enable_follow_up_email_field").show();
        $(".ls_allow_sn_negative_qty_field").show();
        $(".download-full-catalog-btn").show();
        $(".gs_one_standard_field").show();
        this.setGs1FieldOptions([['UPC', 'upc'], ['EAN', 'ean'], ['ISBN', 'isbn'], ['Custom', 'custom']]);
        $(".use_variantfield_mapping_field").show();
    }

    showFormOfPostLog() {
        $(".api_key_field").show();
        $(".error_email_field").show();
    }

    showFormOfLightspeedPos() {
        $(".account_id_field").show();
        $(".store_name_field").show();
        $(".url_field").hide();
    }

    showFormOfLightspeed() {
        $(".account_id_field").show();
        $(".ls_enable_automatic_sync_field").show();
        $(".ls_enable_transfer_slip_commit_field").show();
        $(".ls_enable_sync_custom_fields_automatically_field").show();
        $(".ls_automatic_sync_interval_field").show();
        $(".ls_allow_sn_negative_qty_field").show();
        $(".download-full-catalog-btn").show();
        $(".url_field").hide();
        $(".properties_fields").show();
        $(".gs_one_standard_field").show();
        this.setGs1FieldOptions([['UPC', 'upc'], ['EAN', 'ean'], ['Custom Field: gs1upc', 'gs1upc']]);
        $(".gs_one_standard_text_field").show();
        $(".ls_sync_never_existed_items_in_senitron_to_ext").show();
    }

    showFormOfShopify() {
        $(".url_field").hide();
        $(".domain_prefix_field").show();
        $(".api_key_field").hide();
        $(".account_id_field").hide();
        $(".api_secret_key_field").show();
        $(".token_field").show();
        $(".gs_one_standard_field").show();
        this.setGs1FieldOptions([['UPC', 'upc'], ['EAN', 'ean'], ['Custom Field: gs1upc', 'gs1upc']]);
        $(".shopify_transfer_qty_post_receiving_field").show();
        $(".ls_enable_transfer_slip_commit_field").show();
        $(".download_shopify_images_field").show();
        $(".shopify-custom-price-mapping-field").show();
        $(".shopify-mark-sold-by-payment-status-field").show();
        $(".shopify-webhook-config").show();
        $(".shopify-sync-by-adjustment-field").show();
        $(".shopify-sync-compare-and-swap-field").show();
        $(".shopify-quantity-field-field").show();
        $(".use_metafield_mapping_field").show();
        $(".exclude_from_webhook_auto_recovery_field").show();
        $(".enable_pull_draft_items_field").show();
        $(".use_product_type_as_category_field").show();
        $(".shopify-variant-id-map-column-field").show();
        $(".ls_allow_sn_negative_qty_field").show();
        $(".enable_automatic_pulling_of_purchase_orders_shopify_field").show().find(':input').prop('disabled', false);
        $("#api_secret_key_label").text("Client ID");
        $("#domain_prefix_label").text("Shop Domain");
        $("#token_label").text("Access Token");
    }

    showFormOfTwilio() {
        $(".url_field").hide();
        $(".token_field").show();
        $(".twilio_fields").show();
    }

    showGeneralFields() {
        $(".enabled_field").show();
        $(".url_field").show();
    }

    showCredentialFields() {
        $(".username_field").show();
        $(".password_field").show();
    }

    setGs1FieldOptions(options: [string, string][], selected?: string) {
        const select = $("#gs_one_standard_field");
        const current = String(selected || select.val() || 'upc');
        select.empty();
        options.forEach(([label, value]) => {
            select.append(new Option(label, value, value === current, value === current));
        });
        select.trigger("change.select2");
    }

    enhanceSelects() {
        $("#editIntegration .modal-content > form select.fld").each((_, el) => {
            const select = $(el) as any;
            if (select.hasClass("select2-hidden-accessible")) {
                select.trigger("change.select2");
                return;
            }
            select.select2({
                width: "100%",
                dropdownParent: $("#editIntegration"),
                minimumResultsForSearch: 8
            });
        });
    }

    hideAllElements() {
        $("#domain_prefix_label").text("Domain Prefix");
        $("#token_label").text("Token");
        $("#api_secret_key_label").text("API Secret Key");
        $("#ext_client_secret_label").text("Client Secret Key");
        $("#account_id_label").text("Account ID");
        $(".account_id_field").hide();
        $("#api_secret_key").attr("rows", "3");
        $(".api_secret_key_field").insertAfter(".api_key_field");
        $(".ext_client_secret_field").insertAfter(".database_field");
        $(".api_key_field").hide();
        $(".api_secret_key_field").hide();
        $(".enabled_field").hide();
        $(".url_field").hide();
        $(".port_field").hide();
        $(".error_email_field").hide();
        $(".username_field").hide();
        $(".password_field").hide();
        $(".ls_enable_automatic_sync_field").hide();
        $(".ls_enable_transfer_slip_commit_field").hide();
        $(".ls_automatic_sync_interval_field").hide();
        $(".ls_allow_sn_negative_qty_field").hide();
        $(".ls_enable_sync_custom_fields_automatically_field").hide();
        $(".dispensary_name_field").hide();
        $(".ext_client_id_field").hide();
        $(".ext_client_secret_field").hide();
        $(".license_field").hide();
        $(".domain_prefix_field").hide();
        $(".database_field").hide();
        $(".token_field").hide();
        $(".download-full-catalog-btn").hide();
        $(".store_name_field").hide();
        $(".properties_fields").hide();
        $(".d365_fields").hide().find(":input").prop("disabled", true);
        $(".twilio_fields").hide();
        $(".keonn_fields").hide().find(":input").prop("disabled", true);
        $(".redo_fields").hide().find(":input").prop("disabled", true);
        $("#url_label").text("URL");
        $(".url_field").insertAfter(".password_field");
        $(".port_field").insertAfter(".url_field");
        this.setConnectionHeading(null, null);
        $(".gs_one_standard_field").hide();
        $(".gs_one_standard_text_field").addClass("hide");
        $(".qb_enable_auto_polling_invoices").hide();
        $(".qb_about_field").hide();
        $(".ls_sync_never_existed_items_in_senitron_to_ext").hide();
        $(".shopify_transfer_qty_post_receiving_field").hide();
        $(".download_shopify_images_field").hide();
        $(".shopify-custom-price-mapping-field").hide();
        $(".shopify-mark-sold-by-payment-status-field").hide();
        $(".netsuite_full_catalog_script_field").hide();
        $(".netsuite_catalog_script_field").hide();
        $(".enable_sync_to_netsuite_field").hide();
        $(".queue_name_field").hide();
        $(".shopify-webhook-config").hide();
        $(".enable_creating_missing_catalog_items_field").hide();
        $(".netsuite_qty_download_mode_field").hide().find(":input").prop("disabled", true);
        $(".scope_field").hide();
        $(".company_field").hide();
        $(".shopify_sync_by_adjustment_field").hide();
        $(".shopify-sync-by-adjustment-field").hide();
        $(".shopify-sync-compare-and-swap-field").hide();
        $(".shopify-quantity-field-field").hide();
        $(".acumatica-sync-by-adjustment-field").hide();
        $(".disable_upc_validation").hide();
        $(".use_metafield_mapping_field").hide();
        $(".exclude_from_webhook_auto_recovery_field").hide();
        $(".enable_pull_draft_items_field").hide();
        $(".use_product_type_as_category_field").hide();
        $(".shopify-variant-id-map-column-field").hide();
        $(".pull-shipment-data-field").hide();
        $(".acumatica-pull-shipment-data-text").hide();
        $(".odoo-pull-shipment-data-text").hide();
        $('.acumatica-enable-concurrent-picking-with-shipment').hide();
        $(".odoo-pull-picking-data-field").hide();
        $(".use_variantfield_mapping_field").hide();
        $('.enable_transfer_orders_functionalities').hide();
        $('.enable_automatic_pulling_of_transfer_orders_field').hide();
        $('.enable_item_fulfillment_functionality_for_transfer_orders_field').hide();
        $('.enable_eos_inventory_quantities_importer_field').hide();
        $('.enable_automatic_pulling_of_purchase_orders_netsuite_field').hide().find(':input').prop('disabled', true);
        $('.enable_automatic_pulling_of_purchase_orders_shopify_field').hide().find(':input').prop('disabled', true);
        $('.enable_automatic_pulling_of_sales_orders_field').hide();
        $('.enable_automatic_pulling_of_return_to_vendor_orders_field').hide();
        $('.enable_multi_location_return_to_vendor_orders_field').hide();
        $('.enable_webhook_for_sales_orders_field').hide();
        $('.enable_webhook_for_purchase_orders_field').hide();
        $('.enable_sync_purchase_order_receiving_items_to_netsuite_field').hide();
        $('.enable_6laps_item_filtering_field').hide();
        $('.enable_crm_functionalities').hide();
    }
}

export function LoadPage() {
    InitPage("integrations", () => {
        const integrations = new Integrations();
        integrations.init();
    });
}
